/**
 * Quais produtos de IAP estão LIGADOS neste build — a flag da vitrine nativa.
 *
 * ── Por que a flag é uma LISTA e não um booleano + sub-flags ─────────────────
 *
 * A decisão 3 do blueprint (§1.1) pede os três produtos "cada um atrás de
 * feature flag própria", porque só entra na v1 o que estiver verde em sandbox.
 * A forma óbvia — um `VITE_IAP_ENABLED` mestre mais três chaves — cria um
 * estado contraditório que alguém vai ter de interpretar: mestre desligado com
 * `monthly` ligado significa o quê? Duas fontes da mesma verdade é a doença do
 * BUG-81, e é o mesmo argumento que fez a chave de API do SDK ser o próprio
 * kill switch em vez de ganhar uma flag ao lado (ver `lib/purchases/revenueCat.ts`).
 *
 * Aqui a lista É a verdade: **conjunto vazio significa vitrine nativa
 * desligada**, e não existe um segundo lugar para divergir disso.
 *
 *   VITE_IAP_PRODUCTS=""                        → nada (DEFAULT)
 *   VITE_IAP_PRODUCTS="monthly"                 → só o mensal
 *   VITE_IAP_PRODUCTS="monthly,annual,founder"  → os três
 *
 * ── Sem chave de API não há vitrine ─────────────────────────────────────────
 *
 * `getOfferings()` rejeita se `configure()` nunca rodou, e `configure()` não
 * roda sem `VITE_REVENUECAT_{IOS,ANDROID}_API_KEY`. Ligar os produtos sem a
 * chave entregaria uma tela de compra que nunca carrega preço — pior que tela
 * nenhuma, porque parece defeito em vez de feature desligada. Por isso a
 * disponibilidade exige as duas coisas.
 *
 * ⚠️ Chave AUSENTE no `.env.test` não é chave vazia: o vitest carrega o `.env`
 * e só sobrescreve o que aquele arquivo declara. `VITE_IAP_PRODUCTS` está
 * declarado lá vazio de propósito — sem isso, o `.env` de quem estiver com a
 * vitrine ligada localmente vazaria para a suíte e os guards de "a compra está
 * desligada no nativo" passariam a medir a máquina, não o código. É exatamente
 * o que aconteceu com `VITE_AWIN_*` em 05/08.
 */

import { nativePlatform } from "../../../lib/platform/native";
import { defaultApiKeyFor } from "../../../lib/purchases/revenueCat";

/** Os três produtos do catálogo. Espelha os packages do offering `default`. */
export type IapProduct = "monthly" | "annual" | "founder";

const TODOS: readonly IapProduct[] = ["monthly", "annual", "founder"] as const;

function ehProduto(v: string): v is IapProduct {
  return (TODOS as readonly string[]).includes(v);
}

/**
 * 🔓 TRAVA DO FOUNDER — DESTRAVADA em 19/08, na ordem que o §4.6 fixa.
 *
 * `true` = a **Fase Founder do servidor existe**. `routeEvent`
 * (`functions/src/billing/revenueCatSourcePatch.ts`) devolve
 * `kind: "founder"` para `NON_RENEWING_PURCHASE`, e
 * `runFounderPurchaseTransaction` (`founderFromRevenueCat.ts`) emite o
 * `founderSerial` na transacao que disputa `landing_metrics/founder_counter`
 * com o Stripe — mesmo contador, mesmo cap, um numero por Founder.
 *
 * ── Por que a trava existiu, e por que ela CONTINUA aqui ────────────────────
 *
 * Enquanto o servidor deferia, vender Founder na loja **cobrava o cliente e
 * nao entregava nada**: a App Store cobrava R$ 249, o webhook parkeava, nenhuma
 * fonte era gravada e `isPro` nao mudava. A constante nao foi apagada junto com
 * a trava porque ela virou o INTERRUPTOR dos dois lados: se a Fase Founder
 * precisar ser desligada (rollback do deploy de functions, por exemplo), voltar
 * `false` aqui tira o produto da vitrine, e o guard abaixo cobra que o servidor
 * acompanhe.
 *
 * `tests/unit/founderIapPhaseGuard.test.ts` compara esta constante com o
 * comportamento REAL do `routeEvent` e falha nos DOIS sentidos — ligar aqui sem
 * o servidor quebra, e destravar o servidor sem ligar aqui tambem quebra. E a
 * trava que impede as duas pontas de divergirem em silencio.
 *
 * ⚠️ Ligar esta constante NAO liga a vitrine. Quem decide e `VITE_IAP_PRODUCTS`
 * (default vazio) mais a chave de API do SDK — a decisao 3 do blueprint pede
 * que so entre na v1 o que estiver verde em sandbox ate 27/08, e o Founder e o
 * ultimo da fila (mensal → anual → Founder).
 */
export const FOUNDER_IAP_PHASE_READY = true;

/** O log sai UMA vez por sessao: `enabledIapProducts()` roda a cada render. */
let avisouFounderBloqueado = false;

/**
 * A trava, isolada e com a fase como PARAMETRO.
 *
 * Existe separada por uma razao concreta: `FOUNDER_IAP_PHASE_READY` e uma
 * constante literal, entao com ela em `true` o ramo de bloqueio fica
 * inalcancavel e nenhum teste consegue exercita-lo. Um interruptor de rollback
 * que ninguem consegue testar e um interruptor que so vai ser descoberto
 * quebrado no dia do rollback. Recebendo a fase por parametro, os dois lados
 * ficam cobertos — e o caminho de producao segue lendo a constante.
 *
 * Muta o conjunto recebido e o devolve, para o call site continuar sendo uma
 * linha.
 */
export function aplicarTravaDoFounder(
  pedidos: Set<IapProduct>,
  fasePronta: boolean = FOUNDER_IAP_PHASE_READY,
): Set<IapProduct> {
  // A trava age AQUI, e nao em `isIapProductEnabled`, porque `enabledIapProducts`
  // e a fonte unica: `isNativePurchaseEnabled` deriva dela, entao "so founder
  // ligado" corretamente desliga a vitrine inteira em vez de abrir uma tela sem
  // produto vendavel.
  if (!fasePronta && pedidos.delete("founder") && !avisouFounderBloqueado) {
    avisouFounderBloqueado = true;
    // Degrada seguro em runtime (nao derruba o app) e falha alto onde falhar e
    // de graca: o `native:preflight` bloqueia o build, e o guard de paridade
    // bloqueia o merge.
    console.error(
      "[iap] VITE_IAP_PRODUCTS declara founder com FOUNDER_IAP_PHASE_READY " +
        "= false: o servidor nao esta mintando serial (NON_RENEWING_PURCHASE " +
        "seria deferido) e a loja cobraria sem entregar. Produto IGNORADO — " +
        "ver FOUNDER_IAP_PHASE_READY em iapProducts.ts.",
    );
  }
  return pedidos;
}

/**
 * Produtos ligados neste build. Valor desconhecido na lista é **ignorado em
 * silêncio** de propósito: um typo em env não pode derrubar o app, e o efeito
 * de ignorar é a ausência do produto — que é o lado seguro.
 */
export function enabledIapProducts(): ReadonlySet<IapProduct> {
  const raw = (import.meta.env as Record<string, string | undefined>)
    .VITE_IAP_PRODUCTS;
  if (!raw) return new Set();
  const pedidos = new Set(
    raw
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(ehProduto),
  );

  return aplicarTravaDoFounder(pedidos);
}

/** `true` se ESTE produto pode ser vendido no build nativo atual. */
export function isIapProductEnabled(product: IapProduct): boolean {
  return isNativePurchaseEnabled() && enabledIapProducts().has(product);
}

/**
 * `true` quando o build nativo pode mostrar vitrine: há produto ligado **e**
 * há chave de API para o SDK subir. Na web devolve `false` — quem responde por
 * lá é `purchaseAvailability`, e vender na web nunca dependeu desta flag.
 */
export function isNativePurchaseEnabled(): boolean {
  const platform = nativePlatform();
  if (platform === null) return false;
  if (enabledIapProducts().size === 0) return false;
  return (defaultApiKeyFor(platform)?.trim() ?? "") !== "";
}
